import React, { useState, useEffect, useRef } from 'react';
import { theme, fontStack, bodyFont } from '../theme';
import { FadeIn, Divider, CrossIcon } from '../components/shared';
import churchConfig from '../churchConfig';
import { useChurchData, useChurchInfo } from '../data/ChurchDataProvider';

/* ── Facebook Page Plugin — Responsive iframe embed ── */
function FacebookFeed({ pageUrl, maxWidth = 500, height = 700 }) {
  const containerRef = useRef(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [containerWidth, setContainerWidth] = useState(maxWidth);

  // Measure container width on mount and resize
  useEffect(() => {
    function measure() {
      if (containerRef.current) {
        const w = containerRef.current.offsetWidth;
        // Facebook plugin min width is 180, max is 500
        setContainerWidth(Math.min(maxWidth, Math.max(180, w - 4)));
      }
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [maxWidth]);

  const encodedUrl = encodeURIComponent(pageUrl);
  const iframeSrc = `https://www.facebook.com/plugins/page.php?href=${encodedUrl}&tabs=timeline&width=${containerWidth}&height=${height}&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true`;

  return (
    <div ref={containerRef} style={{ display: 'flex', justifyContent: 'center', position: 'relative', minHeight: 200, width: '100%' }}>
      {!iframeLoaded && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 40, height: 40, border: `2px solid ${theme.border}`,
            borderTopColor: theme.gold, borderRadius: "50%",
            animation: "fbspin 1s linear infinite",
          }} />
          <style>{`@keyframes fbspin { to { transform: rotate(360deg); } }`}</style>
          <span style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted }}>Loading Facebook feed...</span>
        </div>
      )}
      <iframe
        key={containerWidth}
        src={iframeSrc}
        width={containerWidth}
        height={height}
        style={{
          border: 'none', overflow: 'hidden',
          opacity: iframeLoaded ? 1 : 0,
          transition: 'opacity 0.4s ease',
          maxWidth: '100%',
        }}
        scrolling="no"
        frameBorder="0"
        allowFullScreen={true}
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        title="Facebook Page"
        onLoad={() => setIframeLoaded(true)}
      />
    </div>
  );
}

export default function AnnouncementsPage() {
  const { data } = useChurchData();
  const info = useChurchInfo();
  const upcomingEvents = data.newsItems.map(item => ({
      date: item.date,
      title: item.title,
      desc: item.desc,
      category: item.category,
      featured: item.featured,
    }));

  const quickLinks = [
    { label: "Facebook Page", url: info.facebookPageUrl, icon: "📘" },
    { label: "YouTube Channel", url: `https://www.youtube.com/@${info.youtubeHandle}`, icon: "▶️" },
    { label: "E-Giving (Text NPBC to 73256)", url: null, icon: "💝" },
    { label: `Call the Church: ${info.phone}`, url: `tel:${info.phone.replace(/[^0-9]/g, '')}`, icon: "📞" },
  ];

  return (
    <div style={{ paddingTop: 100 }}>
      {/* Header */}
      <section style={{ padding: "80px 24px 40px", textAlign: "center" }}>
        <FadeIn>
          <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 16 }}>Stay Connected</div>
          <h1 style={{ fontFamily: fontStack, fontSize: "clamp(36px, 6vw, 64px)", fontWeight: 300, color: theme.cream, lineHeight: 1.1 }}>
            <span style={{ fontStyle: "italic", fontWeight: 600, color: theme.gold }}>Announcements</span>
          </h1>
          <Divider width={60} />
          <p style={{ fontFamily: bodyFont, fontSize: 16, color: theme.textMuted, maxWidth: 540, margin: "16px auto 0", lineHeight: 1.7 }}>
            Follow along with the latest news, events, and updates from New Prospect Baptist Church. Our Facebook feed keeps you connected between Sundays.
          </p>
        </FadeIn>
      </section>

      {/* Main Content — Facebook Feed + Sidebar */}
      <section style={{ padding: "0 24px 80px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 360px", gap: 40, alignItems: "start" }}>

          {/* ── FACEBOOK FEED (main column) ── */}
          <FadeIn>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#1877F2">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <h2 style={{ fontFamily: fontStack, fontSize: 24, fontWeight: 500, color: theme.cream }}>Latest from Facebook</h2>
              </div>

              <div style={{
                padding: 20, background: theme.bgCard,
                border: `1px solid ${theme.border}`, borderRadius: 4,
                minHeight: 500,
              }}>
                <FacebookFeed
                  pageUrl={info.facebookPageUrl}
                  width={500}
                  height={700}
                />
              </div>

              <p style={{ fontFamily: bodyFont, fontSize: 12, color: theme.textMuted, textAlign: "center", marginTop: 12, lineHeight: 1.6 }}>
                Follow us on Facebook for live service streams, prayer requests, and community updates.
              </p>
            </div>
          </FadeIn>

          {/* ── SIDEBAR ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

            {/* Quick Links */}
            <FadeIn delay={0.1}>
              <div style={{ padding: 28, background: theme.bgCard, border: `1px solid ${theme.border}` }}>
                <h3 style={{ fontFamily: fontStack, fontSize: 20, fontWeight: 500, color: theme.cream, marginBottom: 20 }}>Quick Links</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {quickLinks.map((link, i) => (
                    link.url ? (
                      <a key={i} href={link.url} target={link.url.startsWith("tel:") ? "_self" : "_blank"} rel="noopener noreferrer"
                        style={{
                          display: "flex", alignItems: "center", gap: 12,
                          padding: "10px 14px", background: "rgba(255,255,255,0.02)",
                          border: `1px solid ${theme.border}`,
                          textDecoration: "none", transition: "all 0.3s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = theme.gold; e.currentTarget.style.background = "rgba(200,164,86,0.05)"; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}
                      >
                        <span style={{ fontSize: 18 }}>{link.icon}</span>
                        <span style={{ fontFamily: bodyFont, fontSize: 13, color: theme.cream }}>{link.label}</span>
                      </a>
                    ) : (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: 12,
                        padding: "10px 14px", background: "rgba(255,255,255,0.02)",
                        border: `1px solid ${theme.border}`,
                      }}>
                        <span style={{ fontSize: 18 }}>{link.icon}</span>
                        <span style={{ fontFamily: bodyFont, fontSize: 13, color: theme.cream }}>{link.label}</span>
                      </div>
                    )
                  ))}
                </div>
              </div>
            </FadeIn>

            {/* Upcoming Events */}
            <FadeIn delay={0.2}>
              <div style={{ padding: 28, background: theme.bgCard, border: `1px solid ${theme.border}` }}>
                <h3 style={{ fontFamily: fontStack, fontSize: 20, fontWeight: 500, color: theme.cream, marginBottom: 20 }}>Upcoming Events</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {upcomingEvents.map((ev, i) => (
                    <div key={i} style={{
                      paddingBottom: i < upcomingEvents.length - 1 ? 16 : 0,
                      borderBottom: i < upcomingEvents.length - 1 ? `1px solid ${theme.border}` : "none",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{
                          fontFamily: bodyFont, fontSize: 10, fontWeight: 600,
                          color: theme.gold, letterSpacing: "0.1em", textTransform: "uppercase",
                          padding: "2px 8px", border: `1px solid ${theme.border}`,
                        }}>{ev.category}</span>
                        <span style={{ fontFamily: bodyFont, fontSize: 11, color: theme.textMuted }}>{ev.date}</span>
                      </div>
                      <h4 style={{ fontFamily: fontStack, fontSize: 16, fontWeight: 500, color: theme.cream, marginBottom: 4, lineHeight: 1.3 }}>{ev.title}</h4>
                      <p style={{ fontFamily: bodyFont, fontSize: 12, color: theme.textMuted, lineHeight: 1.6 }}>{ev.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>

            {/* Service Times */}
            <FadeIn delay={0.3}>
              <div style={{
                padding: 28,
                background: `linear-gradient(135deg, rgba(200,164,86,0.08) 0%, ${theme.bgCard} 100%)`,
                border: `1px solid ${theme.borderHover}`,
              }}>
                <h3 style={{ fontFamily: fontStack, fontSize: 20, fontWeight: 500, color: theme.cream, marginBottom: 16 }}>Service Times</h3>
                {data.heroServices.map((s, i) => (
                  <div key={s.id || i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < data.heroServices.length - 1 ? `1px solid ${theme.border}` : "none" }}>
                    <span style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted }}>{s.name}</span>
                    <span style={{ fontFamily: fontStack, fontSize: 16, fontWeight: 500, color: theme.gold }}>{s.time}</span>
                  </div>
                ))}
                <div style={{ marginTop: 16, display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <CrossIcon size={12} color={theme.gold} />
                  <span style={{ fontFamily: bodyFont, fontSize: 12, color: theme.textMuted, lineHeight: 1.5 }}>
                    Join us on Facebook Live every Sunday morning and Wednesday evening.
                  </span>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Responsive CSS for mobile — stack columns */}
      <style>{`
        @media (max-width: 800px) {
          section > div[style*="grid-template-columns"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
